import React, { useMemo, useState } from 'react'
import classNames from 'classnames'

// UIKIT dependencies
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  Button,
  CHIP_TYPES,
  Icon,
  Pagination,
} from '@st-operations/uikit/src/v2/index.js'
import format from '@st-operations/uikit/lib/format.js'
import pretty from '@st-operations/uikit/lib/pretty.js'

// Hooks
import { useModal } from '_hooks'

// Components
// import AudienceKeywordBlocklistModal from '_components/audience-keyword-blocklist-modal'
import AutoscaleTopicsModal from '../autoscale-topics'
import CustomChip from '_components/custom-chip'
import OfflineAttributeModal from '../offline-attribute-modal'
import OverindexingCategoriesSelector from '_components/overindexing-categories-selector'
import SelectedTopics from '../selected-topics/index.jsx'
import TopicAssociatedKeywordsModal from '_components/topic-associated-keywords-modal'
import UploadCSVModal from '_components/upload-csv-modal'
import SignalsCollapsible from '../signals-collapsible'
import TopicsInput from './TopicsInput'

// Services
import { getKeywordsToTopics } from '_services/audience'

// Utilities
import { logErrorAsync } from '_lib/utility'

import './styles.scss'

const getTopicSizeKey = (destinations = []) => {
  const hasShareThis = destinations.some((d) => d.slug?.toLowerCase() === 'sharethis')
  if (hasShareThis || destinations.length === 0) return 'sharethis'
  return destinations[0].slug
}

const getTopicSize = (topic, destinations) =>
  topic?.sizes?.[getTopicSizeKey(destinations)] ?? topic?.sizes?.sharethis

const TOPIC_SORT_ORDER = {
  DESC: 'desc',
  ASC: 'asc',
}

const handleExportTopics = (nonSeedTopics, audience) => () => {
  const csv = nonSeedTopics.map((topic) => {
    return `${topic.name},${getTopicSize(topic, audience.destinations) || ''}`
  })

  csv.unshift('Topic name,Size')

  const blob = new Blob([csv.join('\n')], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.setAttribute('href', url)
  link.setAttribute('download', `${audience.name.split('>').slice(-1)} - topics.csv`)
  link.click()
}

const TOPICS_PAGE_SIZE = 50

const SignalsBlock = ({
  audience,
  setAudience,
  handleInputChange,
  loading,
  setLoading,
  disabledAll,
  nonSeedTopics,
  offlineAttributes,
  restUseSelectedTopics,
}) => {
  const {
    topics,
    selectedTopic,
    selectedTopicForKeywordModal,
    topicsSearch,
    scale,
    preBuiltAttrTypes,
    isLoading: isTopicsLoading,
    handleClearTopicSelection,
    handleSelectTopic,
    handleRemoveTopic,
    handleAddTopic,
    handleAddMultipleTopics,
    handleEditTopicKeywords,
    handleSearchTopicsInputChange,
    handleTopicsKeyDown,
    handleAddOfflineTopics,
    handleRescaleTopics,
    handleScaleChange,
    setSelectedTopicForKeywordModal,
  } = restUseSelectedTopics

  const [topicsPage, setTopicsPage] = useState(0)
  const [topicSortOrder, setTopicSortOrder] = useState(TOPIC_SORT_ORDER.DESC)
  const offlineAttributeModal = useModal(false)
  const topicAssociatedKeywordsModal = useModal(false)
  // const audienceKeywordBlocklistModal = useModal(false)
  const autoscaleTopicsModal = useModal(false)
  const uploadCSVModal = useModal(false)

  const handleOverindexingCategoriesSelectionChange = (items) => {
    const newAudience = {
      ...audience,
      contextualProCategories: items.map(({ value, weight }) => ({ value, weight })),
    }
    if (items?.length === 0) {
      delete newAudience.contextualProCategories
    }
    setAudience(newAudience)
  }

  /**
   * Reads content from first column, ignoring header if called \keyword(s)?\i
   * then request topics sizes and add them to audience
   *
   * @param {String} csv
   */
  const handleUploadCSV = (csv = '') => {
    setLoading((previous) => ({ ...previous, fetchingTopicsSizes: true }))
    const lines = csv.trim().split('\n')
    const hasHeader = lines[0].toLowerCase().includes('keyword')

    let keywords
    if (hasHeader) {
      // eslint-disable-next-line no-unused-vars
      const [_header, ...values] = lines
      keywords = values.map((item) => item.split(',')[0])
    } else {
      keywords = lines.map((item) => item.split(',')[0])
    }

    keywords = keywords.map((keyword) => keyword.replaceAll(' ', '_').toLocaleLowerCase())

    getKeywordsToTopics({
      keywords,
      destinations: audience?.destinations?.map(({ _id }) => _id) ?? [],
      regions: audience?.regions?.map(({ _id }) => _id) ?? [],
    })
      .then(({ topics }) => {
        handleAddMultipleTopics(topics)
      })
      .catch((error) => logErrorAsync(error))
      .finally(() => {
        setLoading((previous) => ({ ...previous, fetchingTopicsSizes: false }))
        uploadCSVModal.handleCloseModal()
      })
  }

  const handleToggleTopicSort = () => {
    setTopicSortOrder((prev) =>
      prev === TOPIC_SORT_ORDER.DESC ? TOPIC_SORT_ORDER.ASC : TOPIC_SORT_ORDER.DESC
    )
  }

  const sortedNonSeedTopics = useMemo(() => {
    if (!topicSortOrder) return nonSeedTopics
    const sorted = [...nonSeedTopics]
    sorted.sort((a, b) => {
      const sizeA = getTopicSize(a, audience.destinations) || 0
      const sizeB = getTopicSize(b, audience.destinations) || 0
      return topicSortOrder === TOPIC_SORT_ORDER.DESC ? sizeB - sizeA : sizeA - sizeB
    })
    return sorted
  }, [nonSeedTopics, topicSortOrder, audience.destinations])

  const getResume = () => {
    if (nonSeedTopics?.length >= 4) {
      return `${nonSeedTopics[0]?.name}, ${nonSeedTopics[1]?.name} and ${
        nonSeedTopics.length - 2
      } more topics`
    } else if (nonSeedTopics?.length === 3) {
      return `${nonSeedTopics[0]?.name}, ${nonSeedTopics[1]?.name} and ${nonSeedTopics[2]?.name}`
    } else if (nonSeedTopics?.length === 2) {
      return `${nonSeedTopics[0]?.name} and ${nonSeedTopics[1]?.name}`
    } else if (nonSeedTopics?.length === 1) {
      return `${nonSeedTopics[0]?.name}`
    }
    return 'No topics'
  }

  return (
    <SignalsCollapsible
      name="Topics"
      dependencies={{ audience }}
      className="ac-signals-block"
      resume={getResume()}
    >
      <div className="ac-signals-block__input-container">
        <p className="ac-signals-block__title">Adding the Topics of your Audience</p>
        <div className="ac-signals-block__add-topics">
          {/* SEARCH TOPICS: */}
          <TopicsInput
            handleSearchTopicsInputChange={handleSearchTopicsInputChange}
            handleTopicsKeyDown={handleTopicsKeyDown}
            topicsSearch={topicsSearch}
            handleScaleChange={handleScaleChange}
            scale={scale}
            audience={audience}
            handleInputChange={handleInputChange}
          />
          {/* FEATURE BUTTONS: */}
          <Button
            size={BUTTON_SIZE.SMALL}
            onClick={autoscaleTopicsModal.handleOpenModal}
            className="ac-signals-block__feature-button"
          >
            <Icon width="18px" height="18px" icon="idea" type="outline" color="currentColor" />
            Autoscale Topics
          </Button>
          <Button
            size={BUTTON_SIZE.SMALL}
            onClick={offlineAttributeModal.handleOpenModal}
            className="ac-signals-block__feature-button"
          >
            <Icon width="18px" height="18px" icon="offline" type="outline" color="currentColor" />
            Prebuilt Topics
          </Button>
        </div>
      </div>
      <div className="ac-signals-block__topics-container">
        {/* SELECTED TOPICS: */}
        <div>
          <div
            className="flex flex-row gap-4 flex-wrap items-center justify-between w-full mb-2 -mt-4"
            style={{ gridArea: 'title' }}
          >
            <If condition={!disabledAll}>
              <label className="text-[14px] font-semibold text-gray-700">Selected Topics</label>
              <div className="flex flex-row gap-2 items-center">
                {/* <Button
                    className="p-0"
                    size={BUTTON_SIZE.SMALL}
                    theme={BUTTON_THEME.GHOST}
                    onClick={audienceKeywordBlocklistModal.handleOpenModal}
                  >
                    Keyword Blocklist
                  </Button> */}
                <If condition={nonSeedTopics?.length > 0}>
                  <Button
                    className="selected-topics__button"
                    size={BUTTON_SIZE.SMALL}
                    theme={BUTTON_THEME.GHOST}
                    onClick={handleToggleTopicSort}
                    title={
                      topicSortOrder === TOPIC_SORT_ORDER.ASC
                        ? 'Sort by size: Low to High'
                        : topicSortOrder === TOPIC_SORT_ORDER.DESC
                        ? 'Sort by size: High to Low'
                        : 'Sort by size'
                    }
                  >
                    <Icon
                      width="18px"
                      height="18px"
                      icon="sort"
                      type="outline"
                      color="currentColor"
                    />
                  </Button>
                  <Button
                    className="p-0"
                    size={BUTTON_SIZE.SMALL}
                    theme={BUTTON_THEME.GHOST}
                    onClick={handleRescaleTopics}
                  >
                    Rescale Topics
                  </Button>
                  <Button
                    className="p-0"
                    size={BUTTON_SIZE.SMALL}
                    theme={BUTTON_THEME.GHOST}
                    onClick={handleClearTopicSelection}
                  >
                    Clear All
                  </Button>
                </If>
                <Button
                  className="selected-topics__button"
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.GHOST}
                  onClick={uploadCSVModal.handleOpenModal}
                  title="CSV Upload"
                >
                  <Icon
                    width="18px"
                    height="18px"
                    icon="share-2"
                    type="outline"
                    color="currentColor"
                  />
                </Button>
                <Button
                  className="selected-topics__button"
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.GHOST}
                  onClick={handleExportTopics(nonSeedTopics, audience)}
                  title="Export topics"
                  disabled={!nonSeedTopics?.length}
                >
                  <Icon
                    width="18px"
                    height="18px"
                    icon="download"
                    type="outline"
                    color="currentColor"
                  />
                </Button>
              </div>
            </If>
          </div>
          <div style={{ gridArea: 'chips' }}>
            <div
              className={classNames('selected-topics__chips', {
                '-mt-12': disabledAll,
              })}
            >
              <Choose>
                <When condition={nonSeedTopics?.length === 0}>
                  <p className="selected-topics__empty-topic">No topics selected</p>
                </When>
                <Otherwise>
                  <For
                    each="topic"
                    of={sortedNonSeedTopics.filter(
                      (_, index) =>
                        index >= topicsPage * TOPICS_PAGE_SIZE &&
                        index < (topicsPage + 1) * TOPICS_PAGE_SIZE
                    )}
                  >
                    <CustomChip
                      className="selected-topics__chip"
                      key={`${topic.name}-${topic._id}`}
                      value={
                        <div className="flex flex-row gap-1 items-center">
                          <If
                            condition={
                              !topic.atoms.seeds && !disabledAll && topic.type !== 'dmp_signal'
                            }
                          >
                            <Icon
                              className={classNames('-ml-2', {
                                'hover:text-gray-400': selectedTopic?._id
                                  ? selectedTopic._id === topic._id
                                  : selectedTopic?.name === topic.name,
                                'hover:text-gray-800': !(selectedTopic?._id
                                  ? selectedTopic._id === topic._id
                                  : selectedTopic?.name === topic.name),
                              })}
                              type="outline"
                              icon="settings"
                              onClick={(e) => {
                                e.stopPropagation()
                                topicAssociatedKeywordsModal.handleOpenModal()
                                setSelectedTopicForKeywordModal(topic)
                              }}
                            />
                          </If>
                          {pretty(topic.name)}{' '}
                          {getTopicSize(topic, audience.destinations)
                            ? format('metric', getTopicSize(topic, audience.destinations))
                            : ''}
                        </div>
                      }
                      onClick={handleRemoveTopic(topic)}
                      disabled={disabledAll}
                      small
                      type={CHIP_TYPES.OUTLINE}
                      selected={
                        selectedTopic?._id
                          ? selectedTopic._id === topic._id
                          : selectedTopic?.name === topic.name
                      }
                      onButtonClick={handleSelectTopic(topic)}
                    />
                  </For>
                </Otherwise>
              </Choose>
            </div>
            <If condition={nonSeedTopics.length > TOPICS_PAGE_SIZE}>
              <Pagination
                className="justify-center mt-4"
                options={new Array(Math.floor(nonSeedTopics.length / TOPICS_PAGE_SIZE) + 1)
                  .fill(0)
                  .map((_, index) => index)}
                page={topicsPage}
                onPageChange={(page) => {
                  setTopicsPage(page.value)
                }}
              />
            </If>
          </div>
        </div>
        {/* SIMILAR TOPICS: */}
        <div>
          <SelectedTopics
            selectedTopic={selectedTopic}
            selectedTopics={nonSeedTopics}
            similarTopics={topics.similarTopics.sort(
              (a, b) => b.sizes.sharethis - a.sizes.sharethis
            )}
            recommendedTopics={topics.recommendedTopics.sort(
              (a, b) => b.sizes.sharethis - a.sizes.sharethis
            )}
            signalTopics={topics.signalTopics.sort((a, b) => b.sizes.sharethis - a.sizes.sharethis)}
            loading={isTopicsLoading}
            onClearSelection={handleClearTopicSelection}
            onAddTopic={handleAddTopic}
            translatedTopics={topics?.translatedTopics || []}
          />
          <OverindexingCategoriesSelector
            audience={audience}
            onSelectionChange={handleOverindexingCategoriesSelectionChange}
          />
        </div>
      </div>
      {/* MODALS: */}
      <TopicAssociatedKeywordsModal
        open={topicAssociatedKeywordsModal.isOpen}
        onClose={topicAssociatedKeywordsModal.handleCloseModal}
        topic={selectedTopicForKeywordModal}
        audience={audience}
        onConfirm={handleEditTopicKeywords}
      />
      {/* <AudienceKeywordBlocklistModal
        open={audienceKeywordBlocklistModal.isOpen}
        onClose={audienceKeywordBlocklistModal.handleCloseModal}
        audience={audience}
        onConfirm={(blocklist) => setAudience({ ...audience, blocklist })}
      /> */}
      <OfflineAttributeModal
        preBuiltAttrTypes={preBuiltAttrTypes}
        offlineAttributes={offlineAttributes}
        isOpen={offlineAttributeModal.isOpen}
        handleClose={offlineAttributeModal.handleCloseModal}
        handleAddOfflineTopics={handleAddOfflineTopics}
      />
      <UploadCSVModal
        isOpen={uploadCSVModal.isOpen}
        handleClose={uploadCSVModal.handleCloseModal}
        onUpload={handleUploadCSV}
        isLoading={loading.fetchingTopicsSizes}
      />
      <AutoscaleTopicsModal
        isOpen={autoscaleTopicsModal.isOpen}
        handleClose={autoscaleTopicsModal.handleCloseModal}
        audience={audience}
        handleAddTopic={handleAddTopic}
        handleAddMultipleTopics={handleAddMultipleTopics}
      />
    </SignalsCollapsible>
  )
}

export default SignalsBlock
